import type { FC } from 'react';
import { useTranslation } from 'react-i18next';

import { Booking, DisplayType } from '@mopla/data-models';

import { RidePointIcon } from '../RidePointIcon';

import {
	InfoWrapper,
	Root,
	RouteLine,
	StationWrapper,
	WithLineWrapper,
} from './DriverTripDescription.styles';
import { LegPassCountInfo } from './LegPassCountInfo';

export interface DriverTripDescriptionProps {
	open: boolean;
	rideStarted: boolean;
	/** Planned time; shown struck through next to the estimated one when delayed. */
	startTime: string;
	/** Currently predicted time — the one rendered as the leg's time. */
	estimatedStartTime: string;
	startPointName: string;
	startAddress?: string;
	startType?: DisplayType;
	endTime: string;
	estimatedEndTime: string;
	endPointName: string;
	endAddress?: string;
	endType?: DisplayType;
	enteringBookings: Booking[];
	exitingBookings: Booking[];
	isDelayed: boolean;
}

export const DriverTripDescription: FC<DriverTripDescriptionProps> = ({
	open,
	rideStarted,
	startTime,
	estimatedStartTime,
	startPointName,
	startAddress,
	startType,
	endTime,
	estimatedEndTime,
	endPointName,
	endAddress,
	endType,
	enteringBookings,
	exitingBookings,
	isDelayed,
}) => {
	const { t } = useTranslation('checkIn');

	/** Both are formatted, so equal strings mean the same shown minute. */
	const showPlannedStart = isDelayed && startTime !== estimatedStartTime;
	const showPlannedEnd = isDelayed && endTime !== estimatedEndTime;

	return (
		<Root data-testid="trip-description-block">
			<StationWrapper
				data-testid="trip-description-departure-station"
				open={open}
				rideStarted={rideStarted}
				specialNeedsCount={
					enteringBookings.filter(
						(booking: Booking) => booking.specialNeeds !== 'NONE'
					).length
				}
				withoutSpecialNeedsCount={
					enteringBookings.filter(
						(booking: Booking) => booking.specialNeeds === 'NONE'
					).length
				}
			>
				<InfoWrapper sx={{ pb: 2 }}>
					<span
						className={showPlannedStart ? 'deviatedTime' : undefined}
						data-testid="trip-description-departure-time"
					>
						{estimatedStartTime}
					</span>
					{showPlannedStart && (
						<span
							className="plannedTime"
							data-testid="driverTripDescription-departurePlannedTime-text"
						>
							{startTime}
						</span>
					)}
					<span>{t('text.departure')}</span>
				</InfoWrapper>
				<WithLineWrapper>
					<RidePointIcon pointType={startType} />
					<RouteLine open={open} rideStarted={rideStarted} />
				</WithLineWrapper>
				<InfoWrapper
					sx={{ pb: 2 }}
					data-testid="trip-description-departure-address"
				>
					<span className="pointName">{startPointName}</span>
					{startAddress && <span className="pointAddress">{startAddress}</span>}
					<LegPassCountInfo bookings={enteringBookings} isEntering />
				</InfoWrapper>
			</StationWrapper>
			<StationWrapper data-testid="trip-description-arrival-station">
				<InfoWrapper>
					<span
						className={showPlannedEnd ? 'deviatedTime' : undefined}
						data-testid="trip-description-arrival-time"
					>
						{estimatedEndTime}
					</span>
					{showPlannedEnd && (
						<span
							className="plannedTime"
							data-testid="driverTripDescription-arrivalPlannedTime-text"
						>
							{endTime}
						</span>
					)}
					<span>{t('text.arrival')}</span>
				</InfoWrapper>
				<RidePointIcon pointType={endType} />
				<InfoWrapper data-testid="trip-description-arrival-address">
					<span className="pointName">{endPointName}</span>
					{endAddress && <span className="pointAddress">{endAddress}</span>}
					<LegPassCountInfo bookings={exitingBookings} />
				</InfoWrapper>
			</StationWrapper>
		</Root>
	);
};

export default DriverTripDescription;
